/**
 * RECYCLE-BOOT — o boot que o PRÓPRIO app pediu, e que o cliente da loja não pode ver (BLK).
 *
 * <p>REQUISITO. Quando o app se recarrega por decisão nossa (mitigação do leak nativo do AVPlay),
 * a tela pode ficar PRETA por alguns segundos — mas NUNCA mostrar UI de sistema
 * ("Sincronizando com servidor", "Carregando conteúdo", "Aguardando conteúdo"). Numa farmácia,
 * texto de debug na tela é pior que um flash preto: o preto passa por "trocando de anúncio".
 *
 * <p>UMA JANELA, NÃO DUAS. A flag guarda o instante do reload. O mesmo intervalo responde às duas
 * perguntas que importam:
 *   1. "esta flag é deste boot?" — se o app morreu logo após marcar e só voltou horas depois
 *      (o cenário travado-por-`prepareAsync`, de 15min a 10h), a flag está velha e é IGNORADA;
 *   2. "já esperei demais?" — se o playback não começou dentro da janela, a UI normal VOLTA.
 * Colapsar as duas na mesma comparação evita duas constantes que podem divergir.
 *
 * <p>🔒 SALVAGUARDA (a parte que não pode ser removida). Sem o teto, uma falha real — cache vazio,
 * sync quebrado, player bloqueado — ficaria escondida atrás de preto permanente, e ninguém
 * diagnostica em campo. Preto curto é melhor que a tela de loading; preto eterno é MUITO pior.
 * O boot medido nesta bancada é ~4,0s, então 20s dá folga de 5× antes de desistir.
 *
 * <p>Funções puras + acesso a localStorage isolado — nenhuma API de plataforma (não viola o
 * desacoplamento validado pelo Semgrep).
 */

export const RECYCLE_BOOT_KEY = 'efflux_recycle_boot_v1';

/**
 * Teto do preto E validade da flag. 20s = ~5× o boot medido (~4,0s) — folga pra sync lento sem
 * deixar a loja no escuro se algo travou de verdade.
 */
export const RECYCLE_BOOT_CAP_MS = 20_000;

/**
 * O boot atual veio de um reload NOSSO e ainda está dentro da janela de preto?
 *
 * <p>Pura de propósito: é a regra que decide se o cliente vê preto ou vê UI, então precisa ser
 * testável sem timers, sem DOM e sem localStorage.
 *
 * @param markedAt instante gravado antes do reload (`null` = boot normal, não é recycle)
 * @param now agora
 */
export function isRecycleBootActive(
  markedAt: number | null,
  now: number,
): boolean {
  if (markedAt == null) return false;
  const age = now - markedAt;
  // `age < 0` = relógio andou pra trás (NTP na TV): trata como flag inválida, não como janela
  // infinita — o caminho seguro é mostrar a UI normal.
  if (age < 0) return false;
  return age < RECYCLE_BOOT_CAP_MS;
}

/** Quanto ainda resta de preto permitido (ms). 0 quando não há janela ativa. */
export function recycleBootRemainingMs(
  markedAt: number | null,
  now: number,
): number {
  if (!isRecycleBootActive(markedAt, now)) return 0;
  return RECYCLE_BOOT_CAP_MS - (now - (markedAt as number));
}

/** Marca a intenção de reload. Chamar IMEDIATAMENTE antes de recarregar. */
export function markRecycleBoot(now: number = Date.now()): void {
  try {
    window.localStorage?.setItem(RECYCLE_BOOT_KEY, String(now));
  } catch {
    /* sem localStorage o reload ainda acontece — só aparece a UI de boot */
  }
}

/** Lê o instante marcado. `null` se ausente ou ilegível. */
export function readRecycleBootAt(): number | null {
  try {
    const raw = window.localStorage?.getItem(RECYCLE_BOOT_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Consome a flag. Chamar quando o playback efetivamente começar (1º frame) — assim um boot
 * seguinte, não solicitado, não herda preto.
 */
export function clearRecycleBoot(): void {
  try {
    window.localStorage?.removeItem(RECYCLE_BOOT_KEY);
  } catch {
    /* ignore */
  }
}

/** ID do cobertor preto — também serve pra não injetar dois. */
export const BLACK_COVER_ID = 'efflux-recycle-black';

/**
 * Pinta preto AGORA, na página que já está indo embora (2026-07-28).
 *
 * <p>POR QUE PRECISA. O `markRecycleBoot` só faz o boot SEGUINTE nascer preto — mas entre a decisão
 * e o documento novo pintar passam ~1,4 s, e nesse intervalo os pixels da página velha continuam na
 * tela. O `preparePageForReload` apaga `<video>`, então quando o último item do loop era vídeo o
 * preto vinha "de graça". Com `<img>` não: a imagem ficava no ar o intervalo inteiro. Sintoma na
 * SSSP: imagem → preto → imagem.
 *
 * <p>DOM CRU, DE PROPÓSITO. Não passa pelo React: a decisão acontece dentro de uma ação de store e
 * precisa virar pixel no mesmo frame, sem esperar reconciliação. A página está morrendo — não há
 * estado pra manter coerente.
 *
 * <p>Não cobre o vídeo do AVPlay: no Tizen a superfície de vídeo é overlay de HARDWARE, por baixo
 * da camada do browser (o mesmo motivo do screenshot sair com placeholder na TV). Fechar o AVPlay
 * pra resolver isso está FORA de cogitação — `stop()` na transição é exatamente o fault nativo
 * revertido na 1.0.272.
 *
 * @returns o instante em que o preto começou — o "t0" real do blackMs.
 */
export function paintBlackNow(now: number = Date.now()): number {
  try {
    if (document.getElementById(BLACK_COVER_ID)) return now;
    const cover = document.createElement('div');
    cover.id = BLACK_COVER_ID;
    cover.style.cssText = [
      'position:fixed',
      'inset:0',
      'top:0',
      'left:0',
      'width:100%',
      'height:100%',
      'background:#000',
      'z-index:2147483647', // acima de TUDO (o topo do stack é 10001 — vide CLAUDE.md)
      'pointer-events:none',
    ].join(';');
    document.body.appendChild(cover);
  } catch {
    /* sem DOM (teste/SSR) — o reload acontece de qualquer forma */
  }
  return now;
}

/** Ring das durações de preto medidas. 20 entradas ≈ cobre horas de reciclagem. */
export const BLACK_LOG_KEY = 'efflux_black_log_v1';
const BLACK_LOG_MAX = 20;

/** Guard de "já medi neste boot" — vide o comentário em {@link recordBlackDuration}. */
let recordedThisBoot = false;

/** Só pra teste — o guard é de módulo, então precisa ser zerável entre casos. */
export function __resetBlackRecordForTest(): void {
  recordedThisBoot = false;
}

export interface BlackLogEntry {
  /** Quando o preto TERMINOU (1º frame na tela). */
  t: number;
  /** Duração do preto em ms — do disparo da reciclagem até o 1º frame. */
  ms: number;
  /** `frame` = terminou com playback real · `cap` = estourou o teto e a UI normal voltou. */
  end: 'frame' | 'cap';
}

/**
 * Grava a duração de um preto. RING PERSISTENTE de propósito.
 *
 * <p>A primeira versão gravava isto no estado do changeSpy, que é POR BOOT — e como cada
 * reciclagem cria um boot novo, cada medição sobrescrevia a anterior. Na bancada de 28/07 houve 12
 * reciclagens e as 12 medições se perderam: sobrou só o boot final, que não tinha tido preto.
 * Ring separado resolve — o histórico atravessa os boots (só reinstalação apaga).
 */
export function recordBlackDuration(ms: number, end: 'frame' | 'cap'): void {
  if (!(ms >= 0) || ms > 120_000) return; // relógio maluco / valor absurdo
  // 🔴 UMA MEDIÇÃO POR BOOT (fix 2026-07-28). A leitura de 28/07 saiu inutilizável — 19 registros
  // pra 6 boots, e valores acima do teto de 20s. Causa: o guard de "já medi" era o `active` do
  // hook, que é POR INSTÂNCIA; qualquer remount do `Stream` criava uma instância nova, que lia a
  // mesma flag e gravava de novo. O boot é a unidade natural da medição, então o guard tem que ser
  // de módulo — vive tanto quanto o processo e não depende do ciclo de vida do React.
  if (recordedThisBoot) return;
  recordedThisBoot = true;
  try {
    const raw = window.localStorage?.getItem(BLACK_LOG_KEY);
    const list: BlackLogEntry[] = raw
      ? (JSON.parse(raw) as BlackLogEntry[])
      : [];
    list.push({ t: Date.now(), ms: Math.round(ms), end });
    window.localStorage?.setItem(
      BLACK_LOG_KEY,
      JSON.stringify(list.slice(-BLACK_LOG_MAX)),
    );
  } catch {
    /* best-effort — telemetria nunca quebra playback */
  }
}

/** Histórico de pretos medidos (diagnóstico/testes). */
export function getBlackLog(): BlackLogEntry[] {
  try {
    const raw = window.localStorage?.getItem(BLACK_LOG_KEY);
    return raw ? (JSON.parse(raw) as BlackLogEntry[]) : [];
  } catch {
    return [];
  }
}
